import { useCallback, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { userAtom, setUserAtom } from '@/store/userAtoms'
import { apis } from '@/modules'
import { UserService } from '@duck/api-definitions'

export type UserSettingKey = keyof UserService.UserSettings

type UserSettingsValues = Required<UserService.UserSettings>

export const useUserSettings = () => {
  const user = useAtomValue(userAtom)
  const setUser = useSetAtom(setUserAtom)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getSetting = useCallback(<K extends UserSettingKey>(
    key: K
  ): UserSettingsValues[K] | undefined => {
    return user?.settings?.[key] as UserSettingsValues[K] | undefined
  }, [user?.settings])

  const setSetting = useCallback(async <K extends UserSettingKey>(
    key: K,
    value: UserSettingsValues[K]
  ): Promise<boolean> => {
    if (!user) {
      setError('User not logged in')
      return false
    }

    setIsUpdating(true)
    setError(null)

    try {
      const updateRequest: UserService.UpdateUserSettingsRequest = {
        [key]: value,
      }

      const { data: updatedUser } = await apis.userService.updateUserSettingsPut(updateRequest)
      setUser(updatedUser)
      return true
    } catch (err) {
      console.error('Failed to update user settings:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update settings'
      setError(errorMessage)
      return false
    } finally {
      setIsUpdating(false)
    }
  }, [user, setUser])

  const setSettings = useCallback(async (
    settings: UserService.UpdateUserSettingsRequest
  ): Promise<boolean> => {
    if (!user) {
      setError('User not logged in')
      return false
    }

    setIsUpdating(true)
    setError(null)

    try {
      const { data: updatedUser } = await apis.userService.updateUserSettingsPut(settings)
      setUser(updatedUser)
      return true
    } catch (err) {
      console.error('Failed to update user settings:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update settings'
      setError(errorMessage)
      return false
    } finally {
      setIsUpdating(false)
    }
  }, [user, setUser])

  return {
    settings: user?.settings,
    getSetting,
    setSetting,
    setSettings,
    isUpdating,
    error,
    clearError: useCallback(() => setError(null), []),
  }
}

